/**
 * Chaînes d'interface (chrome) des pages transferts, par locale.
 * Sépare le texte UI du contenu éditorial (routes.ts / routesEn.ts) pour que
 * les vues FR et EN partagent le même markup (zéro doublon).
 */
import { type I18n } from "@/config/localeText";
import { EVENT_PRICES as P } from "@/(global)/chauffeur/slugs/eventPricing";

/**
 * Ancre de section du formulaire de devis transfert (cible des CTA hero).
 * Définie ICI (module neutre, ni serveur ni client) pour être importée À LA FOIS
 * par la vue serveur (TransferRouteView) ET le formulaire client
 * (TransferQuoteForm). Un `export const` depuis un fichier "use client" devient
 * une référence client côté serveur (se rend en `#function(){throw…}` au lieu de
 * "devis"). À garder ici — règle RSC.
 */
export const TRANSFER_QUOTE_FORM_ID = "devis";

/**
 * Chaînes du formulaire de capture de lead « devis transfert » (Deliverable 3).
 * Toute chaîne visible du composant TransferQuoteForm vient d'ici. Pré-rempli
 * avec le trajet courant (départ → arrivée) ; jamais de prix en dur (lu via
 * getRoutePrices côté composant).
 */
export type TransferQuoteFormStrings = {
  /** En-tête / introduction de la section formulaire. */
  eyebrow: string;
  heading: string;
  subtitle: string;
  /** Réassurance « à partir de X € » (le prix est injecté par le composant). */
  fromPrice: (price: string) => string;
  /** Bloc trajet (récap départ → arrivée). */
  routeLabel: string;
  pickupLabel: string;
  pickupPlaceholder: string;
  dropoffLabel: string;
  dropoffPlaceholder: string;
  /** Champs de la course. */
  dateLabel: string;
  timeLabel: string;
  passengersLabel: string;
  passengersPlaceholder: string;
  /** Coordonnées. */
  firstNameLabel: string;
  firstNamePlaceholder: string;
  lastNameLabel: string;
  lastNamePlaceholder: string;
  emailLabel: string;
  emailPlaceholder: string;
  phoneLabel: string;
  phonePlaceholder: string;
  messageLabel: string;
  messagePlaceholder: string;
  /**
   * Message par défaut composé si la zone de message est vide
   * (≥ 5 car. requis côté API). Reprend le trajet, la date/heure et le nombre
   * de passagers saisis.
   */
  defaultMessage: (input: {
    pickup: string;
    dropoff: string;
    date: string;
    time: string;
    passengers: string;
  }) => string;
  honeypotLabel: string;
  /** Bouton + états. */
  submit: string;
  sending: string;
  successTitle: string;
  successMessage: string;
  errorMessage: string;
  /** Réassurance RGPD / confidentialité + consentement. */
  privacyNote: string;
  consentNote: string;
  /** Libellés requis (validation client). */
  requiredFirstName: string;
  requiredLastName: string;
  requiredEmail: string;
  requiredDate: string;
  invalidPhone: string;
};

export type TransferUiStrings = {
  breadcrumbHome: string;
  breadcrumbChauffeur: string;
  breadcrumbTransfers: string;
  heroEyebrow: string;
  transferPrefix: string;
  factDuration: string;
  factDistance: string;
  factFrom: string;
  onRequest: string;
  answerLabel: string;
  answerText: (
    from: string,
    to: string,
    distanceKm: number,
    durationMin: number,
    price: number | null,
  ) => string;
  ctaBook: string;
  ctaQuote: string;
  ratesHeading: (label: string) => string;
  ratesSubtitle: string;
  ratesFootnote: string;
  whyHeading: string;
  relatedHeading: string;
  relatedSubtitle: (to: string) => string;
  seeAll: string;
  hubTitle: string;
  hubLead: string;
  hubFaqHeading: string;
  hubFaq: { q: string; a: string }[];
  ctaAirport: string;
  allLiaisons: string;
  routeTag: string;
  perMinFrom: (price: string, min: number) => string;
  quoteForm: TransferQuoteFormStrings;
};

export const TRANSFER_UI: I18n<TransferUiStrings> = {
  fr: {
    breadcrumbHome: "Accueil",
    breadcrumbChauffeur: "Chauffeur",
    breadcrumbTransfers: "Transferts",
    heroEyebrow: "Transfert privé avec chauffeur",
    transferPrefix: "Transfert ",
    factDuration: "Durée estimée",
    factDistance: "Distance",
    factFrom: "À partir de",
    onRequest: "Sur devis",
    answerLabel: "En bref",
    answerText: (from, to, dist, dur, price) =>
      `Le transfert privé ${from} ⇄ ${to} couvre environ ${dist} km en ~${dur} min de trajet. Tarif forfaitaire fixe${price != null ? ` dès ${price} €` : " sur devis"}, chauffeur privé, suivi de vol et accueil personnalisé inclus, disponible 24h/24 et 7j/7.`,
    ctaBook: "Réserver ce transfert",
    ctaQuote: "Demander un devis",
    ratesHeading: (label) => `Tarifs forfaitaires ${label}`,
    ratesSubtitle:
      "Prix tout compris par catégorie de véhicule, fixés à l'avance et garantis à la réservation (péages et accueil inclus).",
    ratesFootnote:
      "Tarifs indicatifs susceptibles d'ajustement selon options (attente, arrêts, bagages hors-norme). Le prix final est confirmé par écrit avant la prestation.",
    whyHeading: "Pourquoi ce transfert avec KS Rent Services",
    relatedHeading: "Autres transferts populaires",
    relatedSubtitle: (to) =>
      `Comparez les liaisons les plus demandées vers ${to} et ailleurs sur la Côte d'Azur.`,
    seeAll: "Voir tous les transferts",
    hubTitle: "Transferts VTC sur la Côte d'Azur — tarifs forfaitaires",
    hubLead:
      "Réservez un transfert privé entre Cannes, Nice, Monaco, Saint-Tropez et l'aéroport de Nice. Chauffeur professionnel, suivi de vol et prix fixé à l'avance, sans surprise. Choisissez votre liaison.",
    hubFaqHeading: "Questions fréquentes sur les transferts",
    hubFaq: [
      {
        q: "Comment est calculé le prix d'un transfert privé ?",
        a: `Chaque liaison a un forfait fixe par catégorie de véhicule, annoncé et garanti avant la course (péages et accueil inclus) : aéroport de Nice ⇄ Cannes dès ${P.niceE} €, aéroport de Nice ⇄ Monaco dès ${P.monacoNiceE} €, Cannes ⇄ Monaco dès ${P.cannesMonacoE} € en Classe E. Pas de compteur ni de majoration surprise : le prix final est confirmé par écrit.`,
      },
      {
        q: "Desservez-vous l'aéroport de Nice et les gares ?",
        a: "Oui. Nous assurons les transferts depuis et vers l'aéroport de Nice, les gares TGV (Cannes, Antibes, Nice) et les ports, avec accueil à l'arrivée et suivi de vol ou de train.",
      },
      {
        q: "Que se passe-t-il si mon vol a du retard ?",
        a: "Nous suivons votre numéro de vol en temps réel et ajustons l'heure de prise en charge sans frais supplémentaires : le chauffeur vous attend même en cas de retard.",
      },
      {
        q: "Combien de temps à l'avance faut-il réserver ?",
        a: "Idéalement 24 à 48 h à l'avance, surtout en haute saison et pendant les festivals ; les demandes de dernière minute sont traitées selon disponibilité.",
      },
      {
        q: "Quels véhicules et combien de passagers ?",
        a: "Berline Mercedes (jusqu'à 3 passagers), van 7 places ou véhicule de prestige selon vos bagages et votre groupe. Sièges enfants sur demande.",
      },
      {
        q: "Le transfert peut-il inclure un arrêt ou une attente ?",
        a: "Oui, arrêts intermédiaires et mise à disposition sont possibles et chiffrés à l'avance dans le forfait. KS Rent Services est noté 5,0/5 sur 38 avis Google.",
      },
    ],
    ctaAirport: "Transferts aéroport",
    allLiaisons: "Toutes nos liaisons",
    routeTag: "Transfert privé",
    perMinFrom: (price, min) => `dès ${price} · ~${min} min`,
    quoteForm: {
      eyebrow: "Devis immédiat — transfert privé",
      heading: "Recevez votre devis pour ce transfert",
      subtitle:
        "Indiquez votre date, votre horaire et le nombre de passagers : nous revenons vers vous avec un tarif ferme, péages et accueil inclus, sans surprise.",
      fromPrice: (price) => `À partir de ${price} tout compris`,
      routeLabel: "Votre trajet",
      pickupLabel: "Départ",
      pickupPlaceholder: "Lieu de prise en charge",
      dropoffLabel: "Arrivée",
      dropoffPlaceholder: "Lieu de dépose",
      dateLabel: "Date",
      timeLabel: "Heure",
      passengersLabel: "Passagers",
      passengersPlaceholder: "Nombre de passagers",
      firstNameLabel: "Prénom",
      firstNamePlaceholder: "Votre prénom",
      lastNameLabel: "Nom",
      lastNamePlaceholder: "Votre nom",
      emailLabel: "Email",
      emailPlaceholder: "vous@email.com",
      phoneLabel: "Téléphone (facultatif)",
      phonePlaceholder: "+33 6 12 34 56 78",
      messageLabel: "Message (facultatif)",
      messagePlaceholder:
        "Précisez vos besoins : bagages, vol à suivre, arrêts, siège enfant…",
      defaultMessage: ({ pickup, dropoff, date, time, passengers }) =>
        `Demande de devis — ${pickup} → ${dropoff}${
          date ? `, le ${date}` : ""
        }${time ? ` à ${time}` : ""}${
          passengers ? `, ${passengers} passager(s)` : ""
        }.`,
      honeypotLabel: "Ne pas remplir",
      submit: "Recevoir mon devis",
      sending: "Envoi en cours…",
      successTitle: "Demande envoyée",
      successMessage:
        "Merci ! Votre demande de devis est bien reçue. Notre équipe vous recontacte rapidement avec un tarif ferme.",
      errorMessage:
        "Une erreur est survenue. Réessayez ou écrivez-nous directement.",
      privacyNote:
        "Vos informations restent confidentielles et ne servent qu'à établir votre devis. Aucune transmission à des tiers.",
      consentNote:
        "En envoyant ce formulaire, vous acceptez d'être recontacté au sujet de votre demande de transfert.",
      requiredFirstName: "Le prénom est requis (2 caractères minimum)",
      requiredLastName: "Le nom est requis (2 caractères minimum)",
      requiredEmail: "Un email valide est requis",
      requiredDate: "La date du transfert est requise",
      invalidPhone: "Format de téléphone invalide",
    },
  },
  en: {
    breadcrumbHome: "Home",
    breadcrumbChauffeur: "Chauffeur",
    breadcrumbTransfers: "Transfers",
    heroEyebrow: "Private transfer with chauffeur",
    transferPrefix: "Transfer ",
    factDuration: "Estimated duration",
    factDistance: "Distance",
    factFrom: "From",
    onRequest: "On request",
    answerLabel: "In short",
    answerText: (from, to, dist, dur, price) =>
      `The private ${from} ⇄ ${to} transfer covers about ${dist} km in ~${dur} min. Fixed flat rate${price != null ? ` from €${price}` : " on request"}, private chauffeur, flight tracking and a personal meet-and-greet included, available 24/7.`,
    ctaBook: "Book this transfer",
    ctaQuote: "Request a quote",
    ratesHeading: (label) => `Flat rates ${label}`,
    ratesSubtitle:
      "All-inclusive prices per vehicle category, set in advance and guaranteed at booking (tolls and meet-and-greet included).",
    ratesFootnote:
      "Indicative rates, adjustable depending on options (waiting, stops, oversized luggage). The final price is confirmed in writing before the service.",
    whyHeading: "Why this transfer with KS Rent Services",
    relatedHeading: "Other popular transfers",
    relatedSubtitle: (to) =>
      `Compare the most requested links to ${to} and elsewhere on the French Riviera.`,
    seeAll: "See all transfers",
    hubTitle: "VTC transfers on the French Riviera — flat rates",
    hubLead:
      "Book a private transfer between Cannes, Nice, Monaco, Saint-Tropez and Nice airport. Professional chauffeur, flight tracking and price set in advance, no surprises. Choose your route.",
    hubFaqHeading: "Frequently asked questions about transfers",
    hubFaq: [
      {
        q: "How is the price of a private transfer calculated?",
        a: `Each route has a flat rate per vehicle category, announced and guaranteed before the trip (tolls and meet-and-greet included): Nice airport ⇄ Cannes from €${P.niceE}, Nice airport ⇄ Monaco from €${P.monacoNiceE}, Cannes ⇄ Monaco from €${P.cannesMonacoE} in a Class E. No meter, no surprise surcharge: the final price is confirmed in writing.`,
      },
      {
        q: "Do you serve Nice airport and train stations?",
        a: "Yes. We handle transfers to and from Nice airport, the TGV stations (Cannes, Antibes, Nice) and the ports, with arrivals meet-and-greet and flight or train tracking.",
      },
      {
        q: "What happens if my flight is delayed?",
        a: "We track your flight number in real time and adjust the pick-up at no extra cost: your chauffeur waits even if you land late.",
      },
      {
        q: "How far in advance should I book a transfer?",
        a: "Ideally 24 to 48 hours ahead, especially in high season and during festivals; last-minute requests are handled subject to availability.",
      },
      {
        q: "Which vehicles and how many passengers?",
        a: "A Mercedes saloon (up to 3 passengers), a 7-seat van or a prestige vehicle depending on your luggage and group. Child seats on request.",
      },
      {
        q: "Can the transfer include a stop or waiting time?",
        a: "Yes, intermediate stops and hourly hire are available and priced in advance within the flat rate. KS Rent Services is rated 5.0/5 on 38 Google reviews.",
      },
    ],
    ctaAirport: "Airport transfers",
    allLiaisons: "All our routes",
    routeTag: "Private transfer",
    perMinFrom: (price, min) => `from ${price} · ~${min} min`,
    quoteForm: {
      eyebrow: "Instant quote — private transfer",
      heading: "Get your quote for this transfer",
      subtitle:
        "Share your date, time and number of passengers: we'll come back to you with a firm price, tolls and meet-and-greet included, no surprises.",
      fromPrice: (price) => `From ${price}, all-inclusive`,
      routeLabel: "Your route",
      pickupLabel: "Pickup",
      pickupPlaceholder: "Pickup location",
      dropoffLabel: "Drop-off",
      dropoffPlaceholder: "Drop-off location",
      dateLabel: "Date",
      timeLabel: "Time",
      passengersLabel: "Passengers",
      passengersPlaceholder: "Number of passengers",
      firstNameLabel: "First name",
      firstNamePlaceholder: "Your first name",
      lastNameLabel: "Last name",
      lastNamePlaceholder: "Your last name",
      emailLabel: "Email",
      emailPlaceholder: "you@email.com",
      phoneLabel: "Phone (optional)",
      phonePlaceholder: "+44 20 1234 5678",
      messageLabel: "Message (optional)",
      messagePlaceholder:
        "Tell us more: luggage, flight to track, stops, child seat…",
      defaultMessage: ({ pickup, dropoff, date, time, passengers }) =>
        `Quote request — ${pickup} → ${dropoff}${date ? `, on ${date}` : ""}${
          time ? ` at ${time}` : ""
        }${passengers ? `, ${passengers} passenger(s)` : ""}.`,
      honeypotLabel: "Do not fill",
      submit: "Get my quote",
      sending: "Sending…",
      successTitle: "Request sent",
      successMessage:
        "Thank you! Your quote request has been received. Our team will get back to you shortly with a firm price.",
      errorMessage:
        "Something went wrong. Please try again or email us directly.",
      privacyNote:
        "Your details stay confidential and are only used to prepare your quote. Never shared with third parties.",
      consentNote:
        "By sending this form, you agree to be contacted about your transfer request.",
      requiredFirstName: "First name is required (min 2 characters)",
      requiredLastName: "Last name is required (min 2 characters)",
      requiredEmail: "A valid email is required",
      requiredDate: "The transfer date is required",
      invalidPhone: "Invalid phone format",
    },
  },
  ar: {
    breadcrumbHome: "الرئيسية",
    breadcrumbChauffeur: "سائق خاص",
    breadcrumbTransfers: "عمليات النقل",
    heroEyebrow: "نقل خاص مع سائق",
    transferPrefix: "نقل ",
    factDuration: "المدة المقدّرة",
    factDistance: "المسافة",
    factFrom: "ابتداءً من",
    onRequest: "حسب عرض السعر",
    answerLabel: "باختصار",
    answerText: (from, to, dist, dur, price) =>
      `يغطّي النقل الخاص ${from} ⇄ ${to} نحو ${dist} كم في حوالي ${dur} دقيقة. سعر مقطوع ثابت${price != null ? ` من ${price} €` : " حسب عرض السعر"}، سائق خاص، متابعة الرحلة واستقبال شخصي، متاح على مدار الساعة طوال الأسبوع.`,
    ctaBook: "احجز هذا النقل",
    ctaQuote: "اطلب عرض سعر",
    ratesHeading: (label) => `أسعار مقطوعة ${label}`,
    ratesSubtitle:
      "أسعار شاملة لكل فئة مركبة، محدّدة مسبقًا ومضمونة عند الحجز (الرسوم والاستقبال مشمولان).",
    ratesFootnote:
      "أسعار إرشادية قابلة للتعديل بحسب الخيارات (الانتظار، التوقّفات، الأمتعة غير الاعتيادية). يُؤكَّد السعر النهائي كتابيًّا قبل الخدمة.",
    whyHeading: "لماذا هذا النقل مع KS Rent Services",
    relatedHeading: "عمليات نقل شائعة أخرى",
    relatedSubtitle: (to) =>
      `قارِن أكثر الخطوط طلبًا نحو ${to} وأماكن أخرى على الريفييرا الفرنسية.`,
    seeAll: "اطّلع على جميع عمليات النقل",
    hubTitle: "عمليات نقل خاص على الريفييرا الفرنسية — أسعار مقطوعة",
    hubLead:
      "احجز نقلًا خاصًّا بين كان ونيس وموناكو وسان تروبيه ومطار نيس. سائق محترف، وتتبّع للرحلات، وسعر محدّد مسبقًا دون مفاجآت. اختر خطّك.",
    hubFaqHeading: "أسئلة شائعة حول عمليات النقل",
    hubFaq: [
      {
        q: "كيف يُحتسب سعر النقل الخاص؟",
        a: `لكل مسار سعر مقطوع حسب فئة المركبة، مُعلن ومضمون قبل الرحلة (الرسوم والاستقبال مشمولان): مطار نيس ⇄ كان ابتداءً من ${P.niceE} يورو، مطار نيس ⇄ موناكو ابتداءً من ${P.monacoNiceE} يورو، كان ⇄ موناكو ابتداءً من ${P.cannesMonacoE} يورو في الفئة E. لا عدّاد ولا زيادات مفاجئة: ويُؤكَّد السعر النهائي كتابيًّا.`,
      },
      {
        q: "هل تخدمون مطار نيس والمحطّات؟",
        a: "نعم. نؤمّن النقل من وإلى مطار نيس، ومحطّات القطار السريع (كان، أنتيب، نيس) والموانئ، مع الاستقبال عند الوصول ومتابعة الرحلة أو القطار.",
      },
      {
        q: "ماذا لو تأخّرت رحلتي الجوية؟",
        a: "نتابع رقم رحلتك في الوقت الفعلي ونعدّل موعد الاستقبال دون رسوم إضافية: ينتظرك السائق حتى في حال التأخّر.",
      },
      {
        q: "كم من الوقت مسبقًا يجب الحجز؟",
        a: "يُفضّل من 24 إلى 48 ساعة مسبقًا، خاصة في الموسم المرتفع وأثناء المهرجانات؛ وتُعالَج الطلبات اللحظية بحسب التوافر.",
      },
      {
        q: "ما المركبات وكم عدد الركّاب؟",
        a: "سيدان مرسيدس (حتى 3 ركّاب)، أو فان بسبعة مقاعد، أو مركبة فاخرة بحسب أمتعتك ومجموعتك. مقاعد أطفال عند الطلب.",
      },
      {
        q: "هل يمكن أن يشمل النقل توقّفًا أو انتظارًا؟",
        a: "نعم، التوقّفات الوسيطة والخدمة بالساعة متاحة وتُسعَّر مسبقًا ضمن السعر المقطوع. كي إس رنت سرفيسز حاصلة على تقييم 5,0/5 من 38 مراجعة على Google.",
      },
    ],
    ctaAirport: "عمليات النقل من المطار",
    allLiaisons: "جميع خطوطنا",
    routeTag: "نقل خاص",
    perMinFrom: (price, min) => `ابتداءً من ${price} · نحو ${min} دقيقة`,
    quoteForm: {
      eyebrow: "عرض سعر فوري — نقل خاص",
      heading: "احصل على عرض سعر لهذا النقل",
      subtitle:
        "حدّد تاريخك وموعدك وعدد الركّاب: نعود إليك بسعر ثابت، شاملًا الرسوم والاستقبال، دون مفاجآت.",
      fromPrice: (price) => `ابتداءً من ${price} شامل كلّ شيء`,
      routeLabel: "خطّك",
      pickupLabel: "نقطة الانطلاق",
      pickupPlaceholder: "مكان الاستقبال",
      dropoffLabel: "الوجهة",
      dropoffPlaceholder: "مكان الإنزال",
      dateLabel: "التاريخ",
      timeLabel: "الوقت",
      passengersLabel: "الركّاب",
      passengersPlaceholder: "عدد الركّاب",
      firstNameLabel: "الاسم",
      firstNamePlaceholder: "اسمك",
      lastNameLabel: "اللقب",
      lastNamePlaceholder: "لقبك",
      emailLabel: "البريد الإلكتروني",
      emailPlaceholder: "you@email.com",
      phoneLabel: "الهاتف (اختياري)",
      phonePlaceholder: "+33 6 12 34 56 78",
      messageLabel: "رسالة (اختياري)",
      messagePlaceholder:
        "حدّد احتياجاتك: الأمتعة، رحلة لمتابعتها، التوقّفات، مقعد أطفال…",
      defaultMessage: ({ pickup, dropoff, date, time, passengers }) =>
        `طلب عرض سعر — ${pickup} → ${dropoff}${date ? `، بتاريخ ${date}` : ""}${
          time ? ` في الساعة ${time}` : ""
        }${passengers ? `، ${passengers} راكب` : ""}.`,
      honeypotLabel: "لا تملأ هذا الحقل",
      submit: "أرسِل طلب عرض السعر",
      sending: "جارٍ الإرسال…",
      successTitle: "تم إرسال الطلب",
      successMessage:
        "شكرًا لك! تمّ استلام طلب عرض السعر. سيعاود فريقنا التواصل معك قريبًا بسعر ثابت.",
      errorMessage: "حدث خطأ ما. حاوِل مجدّدًا أو راسِلنا مباشرةً.",
      privacyNote:
        "تبقى معلوماتك سرّية وتُستخدم فقط لإعداد عرض سعرك. لا تُشارَك مع أي طرف ثالث.",
      consentNote:
        "بإرسالك هذا النموذج، توافق على معاودة التواصل معك بشأن طلب النقل الخاص بك.",
      requiredFirstName: "الاسم مطلوب (حرفان كحدّ أدنى)",
      requiredLastName: "اللقب مطلوب (حرفان كحدّ أدنى)",
      requiredEmail: "يلزم بريد إلكتروني صالح",
      requiredDate: "تاريخ النقل مطلوب",
      invalidPhone: "صيغة الهاتف غير صحيحة",
    },
  },
};
